import { ref } from 'vue'
import { message } from 'ant-design-vue'
import { mobileCmsMemberApplicationAPI } from '@/services/axios/perki/mobileCms'

export function useMemberApplicationDetail(id) {
  const loading = ref(false)
  const application = ref(null)
  const applicationHistory = ref([])
  const activeTab = ref('data-diri')

  const tabs = [
    { key: 'data-diri', label: 'Data Diri' },
    { key: 'data-pendidikan', label: 'Data Pendidikan' },
    { key: 'data-pekerjaan', label: 'Data Pekerjaan' },
    { key: 'data-dokumen', label: 'Data Dokumen' },
    { key: 'berkas-pendukung', label: 'Berkas Pendukung' },
  ]

  const STATUS_CONFIG = {
    pending: { label: 'Verifikasi Admin', color: '#0957d9', bg: '#e5f4ff', border: '#91caff' },
    revision: { label: 'Menunggu Revisi', color: '#d57117', bg: '#fff7e5', border: '#ffd591' },
    approved: { label: 'Disetujui', color: '#51a622', bg: '#f6ffed', border: '#b6ea8f' },
    rejected: { label: 'Ditolak', color: '#183e6f', bg: '#fafafa', border: '#d9d9d9' },
  }

  const getStatusConfig = status => STATUS_CONFIG[status] || STATUS_CONFIG.pending

  const loadApplication = async () => {
    loading.value = true
    try {
      const res = await mobileCmsMemberApplicationAPI.getById(id)
      const result = res.data.result
      application.value = result.application ?? result
      applicationHistory.value = result.history ?? []
    } catch (err) {
      console.error('Failed to load application:', err)
    } finally {
      loading.value = false
    }
  }

  const currentTabIndex = () => tabs.findIndex(t => t.key === activeTab.value)

  const goNext = () => {
    const idx = currentTabIndex()
    if (idx < tabs.length - 1) activeTab.value = tabs[idx + 1].key
  }

  const goPrev = () => {
    const idx = currentTabIndex()
    if (idx > 0) activeTab.value = tabs[idx - 1].key
  }

  // Submit actions
  const actionLoading = ref(false)

  // Approve modal
  const showApproveModal = ref(false)
  const approveConfirmed = ref(false)

  const openApprove = () => {
    approveConfirmed.value = false
    showApproveModal.value = true
  }

  const submitApprove = async () => {
    actionLoading.value = true
    try {
      await mobileCmsMemberApplicationAPI.approve(id)
      message.success('Pendaftaran berhasil diterima')
      showApproveModal.value = false
      await loadApplication()
    } catch (err) {
      message.error(err.response?.data?.result || 'Gagal menerima pendaftaran')
    } finally {
      actionLoading.value = false
    }
  }

  // Reject modal
  const showRejectModal = ref(false)
  const rejectNotesInput = ref('')

  const openReject = () => {
    rejectNotesInput.value = ''
    showRejectModal.value = true
  }

  const submitReject = async () => {
    if (!rejectNotesInput.value.trim()) {
      message.warning('Alasan penolakan wajib diisi')
      return
    }
    actionLoading.value = true
    try {
      await mobileCmsMemberApplicationAPI.reject(id, { notes: rejectNotesInput.value.trim() })
      message.success('Pendaftaran berhasil ditolak')
      showRejectModal.value = false
      await loadApplication()
    } catch (err) {
      message.error(err.response?.data?.result || 'Gagal menolak pendaftaran')
    } finally {
      actionLoading.value = false
    }
  }

  // Revisi modal
  const showRevisiModal = ref(false)
  const revisiSections = ref([])
  const revisiNotes = ref('')
  const revisiDropdownOpen = ref(false)

  const revisiOptions = [
    'PERKI Cabang',
    'Surat Rekomendasi PERKI Cabang',
    'Ijazah Dokter Spesialis/Sertifikat Profesi',
    'Surat Keterangan',
    'Pas Foto Warna Terbaru',
    'Foto Profil Formal',
    'Lampiran Persetujuan',
  ]

  const toggleRevisiSection = (opt) => {
    const idx = revisiSections.value.indexOf(opt)
    if (idx === -1) revisiSections.value.push(opt)
    else revisiSections.value.splice(idx, 1)
  }

  const openRevisi = () => {
    revisiSections.value = []
    revisiNotes.value = ''
    revisiDropdownOpen.value = false
    showRevisiModal.value = true
  }

  const submitRevisi = async () => {
    if (!revisiSections.value.length) {
      message.warning('Pilih minimal satu bagian yang perlu direvisi')
      return
    }
    if (!revisiNotes.value.trim()) {
      message.warning('Catatan revisi wajib diisi')
      return
    }
    actionLoading.value = true
    try {
      const notes = `Bagian: ${revisiSections.value.join(', ')}\n\n${revisiNotes.value.trim()}`
      await mobileCmsMemberApplicationAPI.revision(id, { notes })
      message.success('Permintaan revisi berhasil dikirim')
      showRevisiModal.value = false
      await loadApplication()
    } catch (err) {
      message.error(err.response?.data?.result || 'Gagal mengirim permintaan revisi')
    } finally {
      actionLoading.value = false
    }
  }

  return {
    loading,
    application,
    applicationHistory,
    activeTab,
    tabs,
    getStatusConfig,
    loadApplication,
    goNext,
    goPrev,
    actionLoading,
    showApproveModal,
    approveConfirmed,
    openApprove,
    submitApprove,
    showRejectModal,
    rejectNotesInput,
    openReject,
    submitReject,
    showRevisiModal,
    revisiSections,
    revisiNotes,
    revisiOptions,
    revisiDropdownOpen,
    toggleRevisiSection,
    openRevisi,
    submitRevisi,
  }
}
